"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.validateDashboardLayoutWidgetSize = exports.getLayoutWithoutGridHeights = exports.getDashboardLayoutWidgetDefaultGridWidth = exports.getDashboardLayoutItemMaxGridWidth = exports.getDashboardLayoutItemHeightForRatioAndScreen = exports.getResizedItemPositions = exports.splitDashboardLayoutItemsAsRenderedGridRows = exports.unifyDashboardLayoutItemHeights = void 0;
var tslib_1 = require("tslib");
// (C) 2019-2023 GoodData Corporation
var sdk_model_1 = require("@gooddata/sdk-model");
var sdk_ui_ext_1 = require("@gooddata/sdk-ui-ext");
var clamp_1 = tslib_1.__importDefault(require("lodash/clamp"));
var flatten_1 = tslib_1.__importDefault(require("lodash/flatten"));
var isEqual_1 = tslib_1.__importDefault(require("lodash/isEqual"));
var isNil_1 = tslib_1.__importDefault(require("lodash/isNil"));
var round_1 = tslib_1.__importDefault(require("lodash/round"));
var ts_invariant_1 = tslib_1.__importDefault(require("ts-invariant"));
var fluidLayout_1 = require("../../../../_staging/dashboard/fluidLayout");
var layout_1 = require("../../../../_staging/dashboard/fluidLayout/builder/layout");
var layout_2 = require("../../../../_staging/dashboard/fluidLayout/facade/layout");
var sizing_1 = require("../../../../_staging/layout/sizing");
var constants_1 = require("../../../constants");
function unifyDashboardLayoutItemHeights(itemsOrLayout) {
    if (sdk_model_1.isDashboardLayout(itemsOrLayout)) {
        var updatedLayout = tslib_1.__assign(tslib_1.__assign({}, itemsOrLayout), { sections: layout_2.DashboardLayoutFacade.for(itemsOrLayout)
                .sections()
                .reduce(function (acc, section) {
                acc.push(tslib_1.__assign(tslib_1.__assign({}, section.raw()), { items: unifyDashboardLayoutItemHeights(section.items().raw()) }));
                return acc;
            }, []) });
        return updatedLayout;
    }
    var itemsWithSizeForAllScreens = itemsOrLayout.map(function (item) { return (tslib_1.__assign(tslib_1.__assign({}, item), { size: implicitLayoutItemSizeFromXlSize(item.size.xl) })); });
    var itemsWithUnifiedHeightForAllScreens = constants_1.ALL_SCREENS.reduce(function (acc, screen) {
        var itemsAsFutureGridRows = splitDashboardLayoutItemsAsRenderedGridRows(acc, screen);
        return flatten_1.default(itemsAsFutureGridRows.map(function (futureGridRow) {
            return unifyDashboardLayoutItemHeightsForScreen(futureGridRow, screen);
        }));
    }, itemsWithSizeForAllScreens);
    return itemsWithUnifiedHeightForAllScreens;
}
exports.unifyDashboardLayoutItemHeights = unifyDashboardLayoutItemHeights;
/**
 * Derive dashboard layout size for all screens from dashboard layout size defined for xl screen.
 *
 * @param xlSize - dashboard layout size for xl screen
 */
function implicitLayoutItemSizeFromXlSize(xlSize) {
    var xlWidth = xlSize.gridWidth;
    var xlHeight = xlSize.gridHeight;
    var ratio = xlSize.heightAsRatio;
    switch (xlWidth) {
        case 0:
            return dashboardLayoutItemSizeForAllScreens(0, 0, 0, 0, 0, 0, 0);
        case 1:
            return dashboardLayoutItemSizeForAllScreens(ratio, xlHeight, xlWidth, xlWidth, 2, 6, 12);
        case 2:
            return dashboardLayoutItemSizeForAllScreens(ratio, xlHeight, xlWidth, xlWidth, 4, 6, 12);
        case 3:
        case 4:
        case 5:
        case 6:
        case 7:
        case 8:
        case 9:
            return dashboardLayoutItemSizeForAllScreens(ratio, xlHeight, xlWidth, xlWidth, 6, 12, 12);
        case 10:
            return dashboardLayoutItemSizeForAllScreens(ratio, xlHeight, xlWidth, xlWidth, 12, 12, 12);
        case 11:
            return dashboardLayoutItemSizeForAllScreens(ratio, xlHeight, xlWidth, xlWidth, 12, 12, 12);
        case 12:
            return dashboardLayoutItemSizeForAllScreens(ratio, xlHeight, xlWidth, xlWidth, 12, 12, 12);
        default:
            ts_invariant_1.default(false, "Unsupported xlWidth: " + xlWidth);
    }
}
/**
 * Create dashboard layout item size for all screens,
 * with identical height, defined as ratio,
 * but different width, defined as grid items count.
 *
 * @param heightAsRatio - height as ratio to the width, defined in percents
 * @param gridHeight - height as number of grid rows
 * @param xl - width as grid items count for xl screen
 * @param lg - width as grid items count for lg screen
 * @param md - width as grid items count for md screen
 * @param sm - width as grid items count for sm screen
 * @param xs - width as grid items count for xs screen
 */
function dashboardLayoutItemSizeForAllScreens(heightAsRatio, gridHeight, xl, lg, md, sm, xs) {
    if (gridHeight) {
        return {
            xl: {
                gridWidth: xl,
                gridHeight: gridHeight,
            },
            lg: {
                gridWidth: lg,
                gridHeight: gridHeight,
            },
            md: {
                gridWidth: md,
                gridHeight: gridHeight,
            },
            sm: {
                gridWidth: sm,
                gridHeight: gridHeight,
            },
            xs: {
                gridWidth: xs,
                gridHeight: gridHeight,
            },
        };
    }
    return {
        xl: {
            gridWidth: xl,
            heightAsRatio: heightAsRatio,
        },
        lg: {
            gridWidth: lg,
            heightAsRatio: heightAsRatio,
        },
        md: {
            gridWidth: md,
            heightAsRatio: heightAsRatio,
        },
        sm: {
            gridWidth: sm,
            heightAsRatio: heightAsRatio,
        },
        xs: {
            gridWidth: xs,
            heightAsRatio: heightAsRatio,
        },
    };
}
/**
 * Divide the items into a list representing the future rows of the grid.
 * This is useful for performing item transformations, depending on how they really appear in the grid.
 *
 * @param items - dashboard layout items
 * @param screen - responsive screen class
 */
function splitDashboardLayoutItemsAsRenderedGridRows(items, screen) {
    var renderedRows = [];
    var currentRowWidth = 0;
    var currentRow = [];
    items.forEach(function (item) {
        var itemSize = item.size[screen];
        if (isNil_1.default(itemSize)) {
            throw Error("Item size for current screen is undefined");
        }
        if (currentRowWidth + itemSize.gridWidth > fluidLayout_1.DASHBOARD_LAYOUT_GRID_COLUMNS_COUNT) {
            renderedRows.push(currentRow);
            currentRow = [];
            currentRowWidth = 0;
        }
        currentRow.push(item);
        currentRowWidth = currentRowWidth + itemSize.gridWidth;
    });
    if (currentRow.length > 0) {
        renderedRows.push(currentRow);
    }
    return renderedRows;
}
exports.splitDashboardLayoutItemsAsRenderedGridRows = splitDashboardLayoutItemsAsRenderedGridRows;
/**
 * Calculate dashboard layout item height for the provided screen.
 * Result, if custom height is defined, is height of the item, defined
 * as grid items count, multiplied by {@link GRID_ROW_HEIGHT_IN_PX} or width of the item,
 * defined as grid items count, multiplied by height, defined as a ratio.
 *
 * @param item - dashboard layout item
 * @param screen - responsive screen class
 */
function dashboardLayoutItemHeightForScreen(item, screen) {
    var _a, _b;
    var _c = (_b = (_a = item.size) === null || _a === void 0 ? void 0 : _a[screen]) !== null && _b !== void 0 ? _b : {}, gridWidth = _c.gridWidth, gridHeight = _c.gridHeight, _d = _c.heightAsRatio, heightAsRatio = _d === void 0 ? 0 : _d;
    if (!gridWidth) {
        return 0;
    }
    if (gridHeight) {
        return sizing_1.getDashboardLayoutItemHeightForGrid(gridHeight);
    }
    return gridWidth * heightAsRatio;
}
/**
 * Unify dashboard layout items height, defined as ratio, for the provided screen.
 * It overrides height of all items to the highest item height found for the provided screen.
 *
 * @param items - dashboard layout items
 * @param screen -  responsive screen class
 */
function unifyDashboardLayoutItemHeightsForScreen(items, screen) {
    var heights = items.map(function (item) { return dashboardLayoutItemHeightForScreen(item, screen); });
    var maxHeight = Math.max.apply(Math, tslib_1.__spread([0], heights));
    if (maxHeight === 0) {
        return items;
    }
    return items.map(function (item) { return updateDashboardLayoutItemHeight(item, screen, maxHeight); });
}
var updateDashboardLayoutItemHeight = function (item, screen, maxHeight) {
    var _a;
    var itemSizeForCurrentScreen = item.size[screen];
    var heightAsRatio = (itemSizeForCurrentScreen === null || itemSizeForCurrentScreen === void 0 ? void 0 : itemSizeForCurrentScreen.gridWidth) ? round_1.default(maxHeight / itemSizeForCurrentScreen.gridWidth, 2)
        : 0;
    var updatedColumn = item;
    if (!(itemSizeForCurrentScreen === null || itemSizeForCurrentScreen === void 0 ? void 0 : itemSizeForCurrentScreen.gridHeight) &&
        !isNil_1.default(itemSizeForCurrentScreen === null || itemSizeForCurrentScreen === void 0 ? void 0 : itemSizeForCurrentScreen.heightAsRatio) &&
        (itemSizeForCurrentScreen === null || itemSizeForCurrentScreen === void 0 ? void 0 : itemSizeForCurrentScreen.heightAsRatio) !== heightAsRatio) {
        if (sdk_model_1.isWidget(updatedColumn.widget) || sdk_model_1.isWidgetDefinition(updatedColumn.widget)) {
            updatedColumn = tslib_1.__assign(tslib_1.__assign({}, updatedColumn), { widget: tslib_1.__assign({}, updatedColumn.widget) });
        }
        updatedColumn = tslib_1.__assign(tslib_1.__assign({}, updatedColumn), { size: tslib_1.__assign(tslib_1.__assign({}, updatedColumn.size), (_a = {}, _a[screen] = tslib_1.__assign(tslib_1.__assign({}, updatedColumn.size[screen]), { heightAsRatio: heightAsRatio }), _a)) });
    }
    if (screen === "xs" && heightAsRatio > constants_1.DASHBOARD_LAYOUT_MAX_HEIGHT_AS_RATIO_XS) {
        updatedColumn = tslib_1.__assign(tslib_1.__assign({}, updatedColumn), { size: tslib_1.__assign(tslib_1.__assign({}, updatedColumn.size), { xs: tslib_1.__assign(tslib_1.__assign({}, updatedColumn.size.xs), { heightAsRatio: constants_1.DASHBOARD_LAYOUT_MAX_HEIGHT_AS_RATIO_XS }) }) });
    }
    return updatedColumn;
};
/**
 *
 * @internal
 */
exports.getResizedItemPositions = function (originalLayout, resizedLayout, positions) {
    if (positions === void 0) { positions = []; }
    var originalLayoutFacade = layout_2.DashboardLayoutFacade.for(originalLayout);
    return layout_2.DashboardLayoutFacade.for(resizedLayout)
        .sections()
        .reduce(function (acc, section) {
        return section.items().reduce(function (acc, item) {
            var originalColumn = originalLayoutFacade
                .sections()
                .section(section.index())
                .items()
                .item(item.index());
            // if this bombs there is something wrong with the layout
            ts_invariant_1.default(originalColumn);
            var originalContent = originalColumn.widget();
            var updatedContent = item.widget();
            // Is nested layout?
            if (sdk_model_1.isDashboardLayout(originalContent) && sdk_model_1.isDashboardLayout(updatedContent)) {
                return exports.getResizedItemPositions(originalContent, updatedContent, positions);
            }
            if (!isEqual_1.default(originalColumn.size(), item.size()) &&
                (sdk_model_1.isWidget(updatedContent) || sdk_model_1.isWidgetDefinition(updatedContent))) {
                acc.push([item.section().index(), item.index()]);
            }
            return acc;
        }, acc);
    }, positions);
};
exports.getDashboardLayoutItemHeightForRatioAndScreen = function (size, screen) {
    // TODO is this default ok?
    var gridWidth = size.gridWidth, _a = size.heightAsRatio, heightAsRatio = _a === void 0 ? 1 : _a;
    var actualWidth = constants_1.DASHBOARD_LAYOUT_CONTAINER_WIDTHS[screen];
    var actualColumnUnitWidth = actualWidth / fluidLayout_1.DASHBOARD_LAYOUT_GRID_COLUMNS_COUNT;
    return actualColumnUnitWidth * gridWidth * (heightAsRatio / 100);
};
function getDashboardLayoutItemMaxGridWidth(item, screen) {
    var e_1, _a;
    var _b, _c;
    var gridRowWidth = 0;
    var sectionItems = item.section().items().all();
    try {
        for (var sectionItems_1 = tslib_1.__values(sectionItems), sectionItems_1_1 = sectionItems_1.next(); !sectionItems_1_1.done; sectionItems_1_1 = sectionItems_1.next()) {
            var sectionItem = sectionItems_1_1.value;
            var newWidth = sectionItem.sizeForScreen(screen).gridWidth + gridRowWidth;
            if (newWidth <= fluidLayout_1.DASHBOARD_LAYOUT_GRID_COLUMNS_COUNT) {
                if (sectionItem.index() === item.index()) {
                    break;
                }
                gridRowWidth = newWidth;
            }
            else {
                if (sectionItem.index() === item.index()) {
                    return fluidLayout_1.DASHBOARD_LAYOUT_GRID_COLUMNS_COUNT;
                }
                // TODO is this default ok?
                gridRowWidth = (_c = (_b = sectionItem.sizeForScreen(screen)) === null || _b === void 0 ? void 0 : _b.gridWidth) !== null && _c !== void 0 ? _c : 1;
            }
        }
    }
    catch (e_1_1) { e_1 = { error: e_1_1 }; }
    finally {
        try {
            if (sectionItems_1_1 && !sectionItems_1_1.done && (_a = sectionItems_1.return)) _a.call(sectionItems_1);
        }
        finally { if (e_1) throw e_1.error; }
    }
    return fluidLayout_1.DASHBOARD_LAYOUT_GRID_COLUMNS_COUNT - gridRowWidth;
}
exports.getDashboardLayoutItemMaxGridWidth = getDashboardLayoutItemMaxGridWidth;
function getDashboardLayoutWidgetDefaultGridWidth(settings, widgetType, widgetContent) {
    var sizeInfo = sizing_1.getSizeInfo(settings, widgetType, widgetContent);
    return sizeInfo.width.default;
}
exports.getDashboardLayoutWidgetDefaultGridWidth = getDashboardLayoutWidgetDefaultGridWidth;
function getLayoutWithoutGridHeights(layout) {
    var layoutBuilder = layout_1.DashboardLayoutBuilder.for(layout);
    return layoutBuilder
        .modifySections(function (section) {
        return section.modifyItems(function (itemBuilder, itemFacade) {
            var widget = itemFacade.widget();
            if (sdk_model_1.isDashboardLayout(widget)) {
                var updatedLayout = getLayoutWithoutGridHeights(widget);
                return itemBuilder.widget(updatedLayout);
            }
            var itemWithoutGridHeight = removeGridHeightFromItemSize(itemFacade.raw());
            return itemBuilder.setItem(itemWithoutGridHeight);
        });
    })
        .build();
}
exports.getLayoutWithoutGridHeights = getLayoutWithoutGridHeights;
function removeGridHeightFromItemSize(item) {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    var _a = item.size.xl, gridHeight = _a.gridHeight, rest = tslib_1.__rest(_a, ["gridHeight"]);
    return tslib_1.__assign(tslib_1.__assign({}, item), { size: tslib_1.__assign(tslib_1.__assign({}, item.size), { xl: tslib_1.__assign({}, rest) }) });
}
/**
 * @internal
 */
function validateDashboardLayoutWidgetSize(currentWidth, currentHeight, widgetType, widgetContent, settings) {
    var minWidth = sizing_1.getDashboardLayoutWidgetMinGridWidth(settings, widgetType, widgetContent);
    var maxWidth = sdk_ui_ext_1.fluidLayoutDescriptor.gridColumnsCount;
    var minHeight = sizing_1.getDashboardLayoutWidgetMinGridHeight(settings, widgetType, widgetContent);
    var maxHeight = sizing_1.getDashboardLayoutWidgetMaxGridHeight(settings, widgetType, widgetContent);
    var validWidth = currentWidth !== undefined ? clamp_1.default(currentWidth, minWidth, maxWidth) : currentWidth;
    var validHeight = currentHeight !== undefined ? clamp_1.default(currentHeight, minHeight, maxHeight) : currentHeight;
    return {
        validWidth: validWidth,
        validHeight: validHeight,
    };
}
exports.validateDashboardLayoutWidgetSize = validateDashboardLayoutWidgetSize;
//# sourceMappingURL=sizing.js.map